import type { ReactElement } from 'react';
import { useCallback, useId, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';

import { type ImpersonationSession, useImpersonationSnapshot } from './impersonation-state';
import { useImpersonationWriteMode } from './impersonation-write-mode';

const SESSION_KEY_PREFIX = 'cake-impersonation-collapsed:';
const COLLAPSED = '1';
const EXPANDED = '0';

const useCollapsed = (storageKey: string): [boolean, () => void] => {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.sessionStorage.getItem(storageKey) !== EXPANDED;
    } catch {
      return true;
    }
  });

  const toggle = useCallback(() => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      window.sessionStorage.setItem(storageKey, next ? COLLAPSED : EXPANDED);
    } catch {
      // The notice remains usable when browser storage is unavailable.
    }
  }, [collapsed, storageKey]);

  return [collapsed, toggle];
};
const segmentClass = (active: boolean, blocked: boolean) =>
  cn(
    'h-7 w-14 shrink-0 rounded-full border-0 px-2 text-label-sm transition-colors duration-200 motion-reduce:transition-none',
    active && '!bg-od-bg-primary !text-od-content-primary shadow-sm',
    !active && '!bg-transparent',
    !active && (blocked ? '!text-od-content-primary' : '!text-od-content-inverse-primary'),
  );

const BannerContent = ({
  customerUUID,
  session,
}: {
  customerUUID: string;
  session: ImpersonationSession;
}): ReactElement => {
  const {
    blocked,
    viewing,
    viewingName,
    viewingRole,
    requestWriteMode,
    turnOffWriteMode,
    confirmDialog,
  } = useImpersonationWriteMode(session);
  const [collapsed, toggleCollapsed] = useCollapsed(`${SESSION_KEY_PREFIX}${customerUUID}`);
  const explanationId = useId();

  const tone = blocked ? 'bg-amber-300' : 'bg-od-bg-error';
  const textTone = blocked ? 'text-od-content-primary' : 'text-od-content-inverse-primary';

  return (
    <>
      <Box className="fixed top-2 left-1/2 z-5000 w-max max-w-full -translate-x-1/2">
        <Col
          className={cn(
            'gap-2 rounded-3xl p-2 pl-4 shadow-lg transition-[padding] duration-200 motion-reduce:transition-none',
            tone,
            !collapsed && 'gap-3 pb-3',
          )}
        >
          <Row className="items-center gap-2">
            <Text as="p" role="status" className="sr-only">
              You are impersonating {viewing}
              {!blocked && ' in write mode'}
            </Text>

            <Row aria-hidden className="min-w-0 items-center gap-2 max-sm:hidden">
              <Text as="p" className={cn('min-w-0 max-w-64 truncate text-label-md', textTone)}>
                Impersonating {viewingName}
              </Text>
              {viewingRole !== null && (
                <Text
                  as="span"
                  className="shrink-0 rounded-full bg-od-bg-primary px-2 py-0.5 text-label-sm text-od-content-primary"
                >
                  {viewingRole}
                </Text>
              )}
            </Row>

            <Row
              role="group"
              aria-label="Impersonation write mode"
              className="shrink-0 items-center rounded-full bg-od-bg-scrim/15 p-0.5"
            >
              <Button
                variant="secondary"
                size="sm"
                className={segmentClass(blocked, blocked)}
                analyticsName="impersonation-pill-read-only"
                aria-label="Read"
                aria-pressed={blocked}
                onClick={turnOffWriteMode}
              >
                Read
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className={segmentClass(!blocked, blocked)}
                analyticsName="impersonation-pill-write-mode-on"
                aria-label="Write"
                aria-pressed={!blocked}
                onClick={() => {
                  if (blocked) {
                    requestWriteMode();
                  }
                }}
              >
                Write
              </Button>
            </Row>

            <Button
              variant="secondary"
              size="sm"
              className="size-7 shrink-0 rounded-full border-0 bg-transparent p-0"
              analyticsName={
                collapsed ? 'impersonation-pill-expand' : 'impersonation-pill-collapse'
              }
              aria-label={
                collapsed ? 'Expand the impersonation notice' : 'Collapse the impersonation notice'
              }
              aria-expanded={!collapsed}
              aria-controls={collapsed ? undefined : explanationId}
              onClick={toggleCollapsed}
            >
              {collapsed ? <IconChevronDown size={16} /> : <IconChevronUp size={16} />}
            </Button>
          </Row>

          {!collapsed && (
            <Box className="impersonation-notice-expand w-0 min-w-full">
              <Text as="p" id={explanationId} className={cn('pr-2 text-body-sm', textTone)}>
                {blocked
                  ? 'This is their real dashboard. Nothing you change here is saved.'
                  : 'Everything you change saves to their real account.'}
              </Text>
            </Box>
          )}
        </Col>
      </Box>

      {confirmDialog}
    </>
  );
};

export const ImpersonationBanner = ({
  session,
}: {
  session: ImpersonationSession;
}): ReactElement | null => {
  const { customerUUID: impersonatedCustomer, revision } = useImpersonationSnapshot(session);

  if (impersonatedCustomer === null) {
    return null;
  }

  return <BannerContent key={revision} customerUUID={impersonatedCustomer} session={session} />;
};
